import {
  useFloating,
  offset,
  shift,
  flip,
  hide,
  autoUpdate,
} from '@floating-ui/react';
import { isOpenableUrl } from '@littlebird/ui-utils';
import { type Editor, Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { ReactRenderer } from '@tiptap/react';
import { AnimatePresence, motion } from 'motion/react';
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type FormEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { cn } from '@/lib/utils';

import { IconButton } from '../../IconButton';
import { Input } from '../../Input';
import { XStack } from '../../layout';
import {
  useEditorContainer,
  useEditorFloatingContainer,
} from '../base-editor-context';

interface LinkHoverPopoverProps {
  href: string;
  text: string;
  editable: boolean;
  linkElement: HTMLAnchorElement;
  onEdit(newHref: string, newText: string): void;
  onRemove(): void;
  onClose(): void;
  onMouseEnter(): void;
  onMouseLeave(): void;
}

export interface LinkHoverPopoverRef {
  hide(): void;
}

async function openLinkHref(href: string) {
  if (!isOpenableUrl(href)) return;
  // The modal registry imports editors, so load it on demand.
  const { openUntrustedUrl } = await import('@/utils/external-links');
  openUntrustedUrl(href);
}

const LinkHoverPopover = forwardRef<LinkHoverPopoverRef, LinkHoverPopoverProps>(
  function LinkHoverPopover(
    {
      href,
      text,
      editable,
      linkElement,
      onEdit,
      onRemove,
      onClose,
      onMouseEnter,
      onMouseLeave,
    },
    ref
  ) {
    const editorContainer = useEditorContainer();
    const floatingContainer = useEditorFloatingContainer();
    const linkInputRef = useRef<HTMLInputElement>(null);
    const [editHref, setEditHref] = useState(href);
    const [editText, setEditText] = useState(text);
    const [isEditing, setIsEditing] = useState(false);
    const [isOpen, setIsOpen] = useState(true);

    useImperativeHandle(ref, () => ({
      hide() {
        setIsOpen(false);
        setIsEditing(false);
      },
    }));

    const { refs, floatingStyles, update, middlewareData } = useFloating({
      open: isOpen,
      placement: 'bottom-start',
      middleware: [
        offset(4),
        shift({ padding: 8 }),
        flip(),
        ...(editorContainer
          ? [
              hide({
                boundary: editorContainer,
                padding: 8,
              }),
            ]
          : []),
      ],
    });

    useEffect(() => {
      if (!linkElement) return;

      refs.setReference({
        getBoundingClientRect: () => linkElement.getBoundingClientRect(),
        contextElement: linkElement,
      });
    }, [linkElement, refs]);

    useEffect(() => {
      if (!linkElement || !refs.floating.current || !isOpen) return;
      return autoUpdate(linkElement, refs.floating.current, update);
    }, [linkElement, refs, isOpen, update]);

    useEffect(() => {
      setEditHref(href);
      setEditText(text);
    }, [href, text]);

    const handleSave = useCallback(
      (e?: FormEvent) => {
        e?.preventDefault();
        const url = editHref.trim();
        if (!isOpenableUrl(url)) {
          linkInputRef.current?.setCustomValidity(
            'Enter an http or https link.'
          );
          linkInputRef.current?.reportValidity();
          return;
        }
        onEdit(url, editText);
        setIsEditing(false);
        setIsOpen(false);
        onClose();
      },
      [editHref, editText, onEdit, onClose]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setIsEditing(false);
          setIsOpen(false);
          onClose();
        }
      },
      [handleSave, onClose]
    );

    if (!floatingContainer) return null;

    const isHidden = middlewareData.hide?.referenceHidden;

    return createPortal(
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          pointerEvents: isHidden ? 'none' : 'auto',
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(event) => event.stopPropagation()}
      >
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{
                opacity: isHidden ? 0 : 1,
                scale: isHidden ? 0.95 : 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
                transition: {
                  type: 'keyframes',
                  ease: 'easeIn',
                  duration: 0.1,
                },
              }}
              transition={{
                type: 'keyframes',
                ease: 'easeOut',
                duration: 0.15,
              }}
              className={cn(
                'pointer-events-auto',
                'pl-2 pr-1 py-1 z-50 w-[255px] max-w-[255px]',
                'rounded-lg smoothing-default',
                'border border-border-strong',
                'bg-surface',
                'shadow-xl'
              )}
            >
              <AnimatePresence mode="wait" initial={false}>
                {isEditing ? (
                  <motion.form
                    key="update-link-form"
                    initial={{ opacity: 0, height: '6.875rem' }}
                    animate={{ opacity: 1, height: '8.75rem' }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 24,
                    }}
                    className="flex flex-col gap-2 overflow-hidden pl-0.5 pr-1.5 py-1"
                    onSubmit={handleSave}
                  >
                    <div className="flex flex-col gap-2">
                      <div>
                        <label className="text-xs text-foreground-muted mb-1 block">
                          Text
                        </label>
                        <Input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={handleKeyDown}
                          className={cn(
                            'px-1.5 py-1',
                            'h-6',
                            'rounded-md smoothing-default',
                            'focus:outline-none',
                            'w-full'
                          )}
                          placeholder="Link text"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-foreground-muted mb-1 block">
                          Link
                        </label>
                        <Input
                          ref={linkInputRef}
                          type="url"
                          value={editHref}
                          onChange={(e) => {
                            e.currentTarget.setCustomValidity('');
                            setEditHref(e.target.value);
                          }}
                          onKeyDown={handleKeyDown}
                          className={cn(
                            'px-1.5 py-1',
                            'h-6',
                            'rounded-md smoothing-default',
                            'focus:outline-none',
                            'w-full'
                          )}
                          placeholder="https://example.com"
                        />
                      </div>
                    </div>
                    <div className="flex gap-1.5 justify-end mt-0.5">
                      <IconButton
                        type="button"
                        size="xs"
                        tooltip="Cancel"
                        icon={Icons.cancel}
                        onClick={() => {
                          setIsEditing(false);
                          setIsOpen(false);
                          onClose();
                        }}
                      />
                      <IconButton
                        type="submit"
                        size="xs"
                        tooltip="Save"
                        variant="outline"
                        icon={Icons.check}
                      />
                    </div>
                  </motion.form>
                ) : (
                  <motion.div
                    key="button-group"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    className="flex items-center gap-1.5"
                  >
                    <Icons.www size={14} className="text-foreground-muted" />
                    <div className="truncate text-sm text-foreground-subtle flex-1">
                      {href.length ? href : 'Edit to add link'}
                    </div>
                    <XStack className="text-sm items-center gap-1">
                      <IconButton
                        icon={Icons.externalLink}
                        tooltip="Open link"
                        tooltipOptions={{ sideOffset: 9 }}
                        size="xs"
                        variant="ghost"
                        className="hover:bg-surface-active"
                        aria-label="Open link"
                        disabled={!isOpenableUrl(href)}
                        onClick={() => void openLinkHref(href)}
                      />
                      <CopyButton
                        size="xs"
                        text={href}
                        copyText="Copy link"
                        tooltipOptions={{ sideOffset: 9 }}
                        className="focus:outline-none hover:bg-surface-active"
                      />
                      {editable && (
                        <>
                          <IconButton
                            tooltip="Edit link"
                            aria-label="Edit link"
                            tooltipOptions={{ sideOffset: 9 }}
                            icon={Icons.edit}
                            size="xs"
                            variant="ghost"
                            className="hover:bg-surface-active"
                            onClick={() => setIsEditing(true)}
                          />
                          <IconButton
                            tooltip="Remove link"
                            tooltipOptions={{ sideOffset: 9 }}
                            icon={Icons.trash}
                            size="xs"
                            variant="ghost"
                            className="hover:bg-surface-active"
                            onClick={() => {
                              onRemove();
                              setIsOpen(false);
                              onClose();
                            }}
                          />
                        </>
                      )}
                    </XStack>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>,
      floatingContainer
    );
  }
);

function findLinkBoundaries(
  view: EditorView,
  pos: number
): { start: number; end: number } {
  const $pos = view.state.doc.resolve(pos);
  let linkStart = pos;
  let linkEnd = pos;

  const doc = view.state.doc;
  doc.nodesBetween($pos.start(), $pos.end(), (node, nodePos) => {
    if (
      node.isText &&
      node.marks.some((mark) => mark.type === view.state.schema.marks.link)
    ) {
      const textStart = nodePos;
      const textEnd = nodePos + node.nodeSize;

      if (pos >= textStart && pos <= textEnd) {
        linkStart = textStart;
        linkEnd = textEnd;
        return false;
      }
    }
  });

  return { start: linkStart, end: linkEnd };
}

interface PopoverState {
  component: ReactRenderer<LinkHoverPopoverRef, LinkHoverPopoverProps> | null;
  hideTimeout: ReturnType<typeof setTimeout> | undefined;
  isPopoverHovered: boolean;
  linkElement: HTMLElement | null;
  handleMouseLeave: (() => void) | null;
}

function createPopoverState(): PopoverState {
  return {
    component: null,
    hideTimeout: undefined,
    isPopoverHovered: false,
    linkElement: null,
    handleMouseLeave: null,
  };
}

const EXIT_ANIMATION_MS = 100;

function destroyPopover(state: PopoverState, animate = false) {
  if (state.hideTimeout) {
    clearTimeout(state.hideTimeout);
    state.hideTimeout = undefined;
  }

  if (state.linkElement && state.handleMouseLeave) {
    state.linkElement.removeEventListener('mouseleave', state.handleMouseLeave);
  }

  const component = state.component;
  state.component = null;
  state.linkElement = null;
  state.handleMouseLeave = null;
  state.isPopoverHovered = false;

  if (animate && component?.ref) {
    component.ref.hide();
    setTimeout(() => component.destroy(), EXIT_ANIMATION_MS);
  } else {
    component?.destroy();
  }
}

function showPopover(
  state: PopoverState,
  view: EditorView,
  linkElement: HTMLElement,
  editor: Editor
) {
  if (state.linkElement === linkElement && state.component) {
    if (state.hideTimeout) {
      clearTimeout(state.hideTimeout);
      state.hideTimeout = undefined;
    }
    return;
  }

  if (state.component) {
    destroyPopover(state);
  }

  const href = linkElement.getAttribute('href') || '';
  const text = linkElement.textContent || '';
  const pos = view.posAtDOM(linkElement, 0);

  state.linkElement = linkElement;

  const scheduleHide = (delayMs = 500) => {
    if (state.hideTimeout) clearTimeout(state.hideTimeout);
    state.hideTimeout = setTimeout(() => {
      if (!state.isPopoverHovered) {
        destroyPopover(state, true);
      }
    }, delayMs);
  };

  const cancelHide = () => {
    if (state.hideTimeout) {
      clearTimeout(state.hideTimeout);
      state.hideTimeout = undefined;
    }
  };

  const handleLinkMouseLeave = () => scheduleHide();
  state.handleMouseLeave = handleLinkMouseLeave;
  linkElement.addEventListener('mouseleave', handleLinkMouseLeave);

  state.component = new ReactRenderer(LinkHoverPopover, {
    props: {
      href,
      text,
      editable: editor.isEditable,
      linkElement,
      onEdit: (newHref: string, newText: string) => {
        const { tr } = view.state;
        const linkMark = view.state.schema.marks.link.create({
          href: newHref,
        });

        const { start: linkStart, end: linkEnd } = findLinkBoundaries(
          view,
          pos
        );

        tr.insertText(newText, linkStart, linkEnd).addMark(
          linkStart,
          linkStart + newText.length,
          linkMark
        );

        view.dispatch(tr);
      },
      onRemove: () => {
        const { tr } = view.state;
        const { start: linkStart, end: linkEnd } = findLinkBoundaries(
          view,
          pos
        );

        tr.removeMark(linkStart, linkEnd, view.state.schema.marks.link);
        view.dispatch(tr);
      },
      onClose() {
        destroyPopover(state);
      },
      onMouseEnter() {
        state.isPopoverHovered = true;
        cancelHide();
      },
      onMouseLeave() {
        state.isPopoverHovered = false;
        scheduleHide(500);
      },
    },
    editor,
  });
}

interface LinkHoverOptions {
  openOnReadOnlyClick: boolean;
}

export const LinkHoverExtension = Extension.create<LinkHoverOptions>({
  name: 'linkHover',

  addOptions() {
    return { openOnReadOnlyClick: false };
  },

  addStorage() {
    return {
      popoverState: createPopoverState(),
      visibilityObserver: null as IntersectionObserver | null,
    };
  },

  onDestroy() {
    destroyPopover(this.storage.popoverState);
    this.storage.visibilityObserver?.disconnect();
    this.storage.visibilityObserver = null;
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    const editor = this.editor;
    const { openOnReadOnlyClick } = this.options;

    // Watch editor DOM visibility to destroy popover on collapse/scroll-out
    storage.visibilityObserver?.disconnect();
    storage.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && storage.popoverState.component) {
          destroyPopover(storage.popoverState);
        }
      },
      { threshold: 0 }
    );
    storage.visibilityObserver.observe(editor.view.dom);

    return [
      new Plugin({
        key: new PluginKey('linkHover'),
        props: {
          handleDOMEvents: {
            click: (view, event) => {
              const linkElement = (event.target as HTMLElement).closest(
                'a[href]'
              );
              if (!linkElement) return false;
              event.preventDefault();
              event.stopPropagation();
              if (!editor.isEditable) {
                if (openOnReadOnlyClick)
                  void openLinkHref(linkElement.getAttribute('href') ?? '');
                return true;
              }
              showPopover(
                storage.popoverState,
                view,
                linkElement as HTMLAnchorElement,
                editor
              );
              return true;
            },
            mouseover: (view, event) => {
              const target = event.target as HTMLElement;
              const linkElement = target.closest(
                'a[href]'
              ) as HTMLAnchorElement;

              if (linkElement) {
                showPopover(storage.popoverState, view, linkElement, editor);
                return true;
              }

              return false;
            },
          },
        },
      }),
    ];
  },
});
